/**
 * INACTIVITY-EXPLANATION-001 — Add / edit documentary explanations for an
 * employee's inactivity periods without leaving the Inactivity Report.
 * Explanations never change durations or totals (display + audit only).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, CalendarDays, UserRound, CheckCircle2, ExternalLink, Loader2, MessageSquareText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { formatDateTimeET, formatDateOnly } from '../../utils/formatters';
import { fmtT, fmtMinLong } from './inactivitySummary';

const API = process.env.REACT_APP_BACKEND_URL || '';
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const periodKey = (g) => `${g.inactive_from}|${g.inactive_to}`;

export const InactivityExplanationDialog = ({ open, onOpenChange, emp, date, canDowntime, onSaved }) => {
  const navigate = useNavigate();
  const periods = useMemo(() => (emp?.gaps || []).filter((g) => !g.resolved).slice().sort((a, b) => new Date(a.inactive_from) - new Date(b.inactive_from)), [emp]);
  const [selected, setSelected] = useState([]);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const unexplained = periods.filter((g) => !g.explanation).map(periodKey);
    setSelected(unexplained.length ? unexplained : periods.map(periodKey));
    setText('');
  }, [open, periods]);

  useEffect(() => {
    if (selected.length === 1) {
      const g = periods.find((p) => periodKey(p) === selected[0]);
      if (g?.explanation?.explanation) setText(g.explanation.explanation);
    }
  }, [selected, periods]);

  const toggle = (k) => setSelected((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const allSelected = selected.length === periods.length && periods.length > 0;
  const canSave = selected.length > 0 && text.trim().length >= 3 && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const chosen = periods.filter((g) => selected.includes(periodKey(g)));
      const { data } = await axios.post(`${API}/api/reports/inactivity-explanations`, {
        user_id: emp.user_id, date,
        periods: chosen.map((g) => ({ inactive_from: g.inactive_from, inactive_to: g.inactive_to, gap_minutes: g.gap_minutes, unexplained_minutes: g.unexplained_minutes })),
        explanation: text.trim(),
      }, { headers: authH() });
      toast.success(`Explanation saved for ${chosen.length} period${chosen.length === 1 ? '' : 's'}`);
      onSaved?.(emp.user_id, data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save explanation');
    } finally {
      setSaving(false);
    }
  };

  if (!emp) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden rounded-2xl border-slate-200" data-testid="inactivity-explanation-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 bg-gradient-to-r from-blue-50 via-indigo-50 to-violet-50 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl bg-white text-blue-600 grid place-items-center shadow-sm"><MessageSquareText className="h-5 w-5" /></span>
            <div className="min-w-0">
              <DialogTitle className="text-[16px] font-bold text-slate-900">Add Explanation</DialogTitle>
              <DialogDescription className="text-[12.5px] text-slate-600 flex items-center gap-3 mt-0.5 flex-wrap">
                <span className="inline-flex items-center gap-1.5 font-medium text-slate-800" data-testid="explanation-dialog-employee"><UserRound className="h-3.5 w-3.5 text-blue-500" />{emp.name}</span>
                <span className="inline-flex items-center gap-1.5" data-testid="explanation-dialog-date"><CalendarDays className="h-3.5 w-3.5 text-violet-500" />{formatDateOnly(date)}</span>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="px-5 py-4 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Inactivity periods · {periods.length}</span>
              {periods.length > 1 && (
                <button type="button" className="text-[12px] font-semibold text-blue-600 hover:text-blue-700" onClick={() => setSelected(allSelected ? [] : periods.map(periodKey))} data-testid="explanation-select-all">
                  {allSelected ? 'Clear all' : 'Select all'}
                </button>
              )}
            </div>
            <ul className="space-y-1.5 max-h-56 overflow-y-auto pr-1" data-testid="explanation-period-list">
              {periods.map((g, i) => {
                const k = periodKey(g);
                const on = selected.includes(k);
                return (
                  <li key={k}>
                    <label className={`flex items-center gap-3 rounded-xl border px-3 py-2 cursor-pointer transition-colors duration-150 ${on ? 'border-blue-300 bg-blue-50/70' : 'border-slate-200 bg-white hover:bg-slate-50'}`} data-testid={`explanation-period-${i}`}>
                      <Checkbox checked={on} onCheckedChange={() => toggle(k)} data-testid={`explanation-period-check-${i}`} />
                      <span className="h-5 w-5 rounded-full bg-red-100 text-red-600 text-[10.5px] font-bold grid place-items-center shrink-0">{i + 1}</span>
                      <span className="flex-1 min-w-0 text-[13px] text-slate-800 flex items-center gap-2 flex-wrap">
                        <Clock className="h-3.5 w-3.5 text-slate-400" />{fmtT(g.inactive_from)} – {fmtT(g.inactive_to)}
                        {g.explanation && <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Explained</span>}
                      </span>
                      <span className="text-[12.5px] font-semibold text-slate-700 tabular-nums">{fmtMinLong(g.unexplained_minutes)}</span>
                    </label>
                    {g.explanation && on && selected.length > 1 && (
                      <p className="ml-11 mt-1 text-[11.5px] text-slate-500 truncate" title={g.explanation.explanation}>Current: “{g.explanation.explanation}” · {g.explanation.updated_by_name}</p>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1.5" htmlFor="inactivity-explanation-text">Explanation</label>
            <Textarea id="inactivity-explanation-text" value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={2000}
              placeholder="Why was the employee inactive during the selected period(s)? e.g. offline client call, courthouse visit, system outage…"
              className="text-[13px] rounded-xl resize-none" data-testid="explanation-text" />
            <div className="flex items-center justify-between mt-1 text-[11px] text-slate-400">
              <span>{selected.length} period{selected.length === 1 ? '' : 's'} selected · recorded durations are never changed</span>
              <span>{text.trim().length}/2000</span>
            </div>
            {selected.length === 1 && periods.find((p) => periodKey(p) === selected[0])?.explanation && (
              <p className="mt-1 text-[11.5px] text-amber-700" data-testid="explanation-edit-hint">Editing an existing explanation — the previous version is kept in the audit history.</p>
            )}
          </div>
        </div>

        <DialogFooter className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex-row items-center justify-between sm:justify-between gap-2">
          {canDowntime ? (
            <button type="button" className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-slate-500 hover:text-blue-600 transition-colors" data-testid="explanation-record-downtime-link"
              onClick={() => { onOpenChange(false); navigate(`/analytics/downtime?user_id=${emp.user_id}&date=${date}`); }}>
              <ExternalLink className="h-3.5 w-3.5" /> Record Downtime instead
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <Button variant="ghost" className="h-9 text-[13px]" onClick={() => onOpenChange(false)} disabled={saving} data-testid="explanation-cancel-btn">Cancel</Button>
            <Button className="h-9 text-[13px] bg-blue-600 hover:bg-blue-700 shadow-sm" onClick={save} disabled={!canSave} data-testid="explanation-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Save Explanation
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ExplanationAudit = ({ emp }) => {
  const list = emp?.explanations || [];
  if (!list.length && !emp?.review) return null;
  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3" data-testid="explanation-audit">
      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Explanation & Review Audit</div>
      {emp?.review && (
        <div className="text-[12px] text-slate-600 mb-2" data-testid="explanation-audit-review">
          Reviewed by <span className="font-semibold text-slate-800">{emp.review.reviewed_by_name}</span> ({emp.review.reviewed_by_role}) · {formatDateTimeET(emp.review.reviewed_at)} · status now <span className="font-semibold">{(emp.review_status || '').replace(/_/g, ' ')}</span>
        </div>
      )}
      <ul className="space-y-1.5">
        {list.map((x) => (
          <li key={x.id} className="text-[12px] text-slate-700 flex flex-wrap gap-x-2" data-testid="explanation-audit-row">
            <span className="font-semibold tabular-nums">{fmtT(x.inactive_from)} – {fmtT(x.inactive_to)}</span>
            <span className="text-slate-400">·</span><span className="italic">“{x.explanation}”</span>
            <span className="text-slate-400">·</span>
            <span>v{x.version || 1} · {x.updated_by_name} ({x.updated_by_role}) · {formatDateTimeET(x.updated_at)}</span>
            {x.version > 1 && <span className="text-slate-400">· created {formatDateTimeET(x.created_at)} by {x.created_by_name}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default InactivityExplanationDialog;
